/**
 * Matching the enquiry's lines to catalogue items WITHOUT waiting for a click.
 *
 * ---- Why this writes rather than suggests ----
 *
 * The matcher's answer used to sit on the row as a proposal: the purchaser read it, agreed with
 * it, and clicked. On a sixty-line enquiry that is sixty clicks to agree with something already
 * decided. The line's item is now set from the top-ranked match as soon as the matcher answers,
 * and changing it is the same act it always was — open the item picker on the row.
 *
 * This is a real write to a real document, made without anybody asking for it, so what it must
 * NOT touch matters more than what it does:
 *
 *   - a line that already has a RESOLVABLE item — never, at any score. The purchaser's own choice,
 *     an earlier auto-match, and a code the customer wrote all look the same here, and all of them
 *     outrank a similarity ranking.
 *
 *     "Already decided" means a code the CATALOGUE RESOLVES, not merely a non-empty string. The AI
 *     extraction sometimes writes a DESCRIPTION into `item_code` — live, 2026-09-03: a line
 *     reading `saved as "General Purpose Glue, 50ml"` — and treating that as a decision meant the
 *     one line on the enquiry that most needed matching was the one line this refused to touch,
 *     while every other row matched itself. See `unresolvableCodes`.
 *   - a line already attempted this session (`attempted`) — so clearing an item does not have it
 *     written straight back, and a write that FAILED is not retried in a loop.
 *   - a line the matcher had no candidate for.
 *
 * ---- On confidence ----
 *
 * There is deliberately no score floor. `searchMatch.ts` explains why one cannot be set honestly:
 * the score is not comparable across queries, and a correct top-ranked match scores anywhere from
 * 0.11 to 0.86. A floor would silently skip correct answers while still admitting wrong ones.
 * Instead every low-scoring match is written and FLAGGED — `lowConfidence` rides through to the
 * row, which keeps saying "low confidence" after the write, and to the summary the purchaser
 * reads once ("12 matched · 5 low-confidence — worth checking").
 */

import type { EnquiryItem } from '../../lib/types'
import type { ItemMatch } from '../../lib/searchMatch'

export interface AutoMatchAction {
  lineId: string
  itemCode: string
  /** The matcher was unsure — the row stays marked and the summary counts it. */
  lowConfidence: boolean
  /**
   * The matcher's own score for this item, PERSISTED with the match.
   *
   * The matcher is only ever asked about lines that still lack an item, so the moment a line is
   * matched its score exists nowhere else — reload the page and the CONF. column had nothing left
   * to read but `extraction_confidence`, which the mail reader stamps as 1 on every line. A line
   * matched at 3% therefore rendered a full green 100% bar. Writing the score with the match is
   * what fixes that, and `extraction_confidence` is exactly the field for it: the column has only
   * ever claimed to measure confidence in the ITEM.
   */
  score: number
}

export function linesToAutoMatch(
  lines: EnquiryItem[],
  matchFor: (line: EnquiryItem) => ItemMatch | undefined,
  attempted: ReadonlySet<string>,
  /**
   * Codes the caller has POSITIVELY established the catalogue does not resolve — the same fact
   * `LineRow` renders as "not in catalogue". A code that is merely still loading is deliberately
   * absent, and so is still treated as decided: matching over a real code before its master
   * arrives would destroy a good value, which is far worse than a line matching a moment late.
   */
  unresolvableCodes: ReadonlySet<string> = new Set(),
): AutoMatchAction[] {
  const plan: AutoMatchAction[] = []
  for (const line of lines) {
    // Already decided — but only if the code means something. See the header.
    if (line.item_code && !unresolvableCodes.has(line.item_code)) continue
    if (attempted.has(line.line_id)) continue
    const best = matchFor(line)?.candidates[0]
    if (!best) continue
    plan.push({
      lineId: line.line_id,
      itemCode: best.item_code,
      lowConfidence: matchFor(line)?.lowConfidence ?? false,
      score: best.score,
    })
  }
  return plan
}

/**
 * Codes the item master was ASKED for and did not have — the AI extraction sometimes writes a
 * description into `item_code`, and a line carrying one is undecided rather than decided.
 *
 * `resolvedCodes` is what the answer on hand actually covers, and it is the whole point: while a
 * changed code set is being read, `useItemsByCode` keeps the previous answer on screen, so a
 * freshly picked code is absent from `byCode` without being missing from the catalogue. Judging
 * it there had the auto-matcher call the line undecided and write its own candidate over the
 * purchaser's choice a second after they made it (2026-09-23).
 */
export function codesMissingFromMaster(
  lines: readonly { item_code: string | null }[],
  byCode: ReadonlyMap<string, unknown>,
  resolvedCodes: ReadonlySet<string>,
): Set<string> {
  const missing = new Set<string>()
  for (const line of lines) {
    const code = line.item_code
    if (!code || !resolvedCodes.has(code) || byCode.has(code)) continue
    missing.add(code)
  }
  return missing
}
